import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import useNotifications from '~/context/Notifications';
import { useClientSessionQuery } from '~/hooks/queries/useSessionPlQuery';
import { customFetch } from '~/utils/customFetch';

export const geoArray = ['us', 'uk', 'ca', 'au'];
// Make sure this union matches with the above array. There's no good inbuilt way to map the array to the union (https://github.com/colinhacks/zod/issues/831)
const validGeoParameterSchema = z.union([
  z.literal('gb').or(z.literal('uk')),
  z.literal('us'),
  z.literal('au'),
  z.literal('ca'),
]);

const forecastData = z
  .object({
    partnerId: z.number(),
    partnerName: z.string(),
    percent: z.number(),
  })
  .array();

export type ForecastData = z.infer<typeof forecastData>;

const forecastErrorResponseSchema = z.object({
  message: z.string(),
});

const validGeoSchema = z.array(validGeoParameterSchema);

export type ValidGeoSchema = z.infer<typeof validGeoSchema>;

export function useForecastQuery(geos: ValidGeoSchema, excludedPartnerIds: string[]) {
  const clientSessionQuery = useClientSessionQuery();
  const { setTimedNotification } = useNotifications();
  return useQuery({
    queryKey: ['forecast', clientSessionQuery.data?.id, geos, excludedPartnerIds],
    queryFn: () => {
      const params = new URLSearchParams();
      geos.forEach((geo) =>
        geo === 'uk' ? params.append('geo', 'gb') : params.append('geo', geo),
      );
      excludedPartnerIds.forEach((partnerId) =>
        params.append('ex_partner_id', partnerId.toString()),
      );
      return customFetch(
        `/authenticated/client/${clientSessionQuery.data?.id}/bookings/forecaster`,
        {
          method: 'get',
          params,
          useNodeEndpoint: true,
          throwOnHTTPError: false,
        },
      ).then(({ data, status, statusText }) => {
        if (status >= 400 && status < 500) {
          const error = forecastErrorResponseSchema.parse(data);
          setTimedNotification('danger', error.message, 5000);
          throw new Error(error.message);
        }
        if (status >= 500) {
          setTimedNotification('danger', 'Something went wrong, please try again', 5000);
          throw new Error(statusText);
        }
        const success = forecastData.parse(data);
        let total = 0;
        for (const item of success) {
          total += Math.round(item.percent);
        }
        // specifically !== as it can be either way, over or under
        if (Math.round(total) !== 100) {
          const diff = 100 - total;
          const firstWithPercent = success.find((item) => item.percent > 0);
          if (firstWithPercent) {
            firstWithPercent.percent += diff;
          }
        }
        return success;
      });
    },
    enabled: !!clientSessionQuery.data?.id && geos.length > 0,
    placeholderData: keepPreviousData,
  });
}
